import { api, type LabelScore } from '../lib/api.ts'
import { RunMetric, RunModal } from './RunModal.tsx'
import { useGoldenRun } from '../lib/useGoldenRun.ts'
import { ScoreRows } from './ScoreRows.tsx'

/**
 * Reading one case again, and showing where that landed against its label.
 *
 * The light counterpart to *Simular*, which pushes the page through the whole
 * pipeline and leaves a simulation conferência behind. The question people
 * actually ask of a label -- can we read this, and is the label right? -- does
 * not need a conferência to answer.
 *
 * Nothing is kept. `eval:reader` is where a number becomes a measurement, and
 * it averages several runs because one sample of a model is not one: three
 * runs of an unchanged prompt against unchanged labels have scored 0.704,
 * 0.717 and 0.654. A single figure here is a look, and the modal says so.
 */
export function GoldenCaseRun({ caseId, onClose }: { caseId: string; onClose: () => void }) {
  /*
   * Started, then followed.
   *
   * The reading used to happen inside the POST -- 35.7 s for hw-easy-01 -- and
   * Bun closes a socket that has sent nothing for ten seconds, so this modal
   * reported "a requisição falhou (502)" about a run the server had completed.
   */
  const { result, error, busy } = useGoldenRun<{
    score: LabelScore
    draft: boolean
    detected: boolean
  }>(
    () => api.runGoldenCase(caseId),
    async (runId) => {
      const run = await api.goldenRun(caseId, runId)
      return { status: run.status, result: run.result ?? undefined, error: run.error }
    },
    caseId,
  )

  const pct = (n: number) => `${Math.round(n * 100)}%`
  const score = result?.score

  return (
    <RunModal
      title="Leitura do caso"
      caseId={caseId}
      busy={busy}
      error={error}
      metrics={
        score === undefined ? undefined : (
          <div className="rounded-base border-stroke-weak flex flex-wrap gap-6 border p-3">
            <RunMetric label="Geral" value={pct(score.overall)} />
            <RunMetric label="Ativos" value={pct(score.ingredients.f1)} />
            <RunMetric label="Doses" value={pct(score.doses.f1)} />
            <RunMetric
              label="Preparações"
              value={`${score.grouping.actual}/${score.grouping.expected}`}
              tone={score.grouping.ok ? 'ok' : 'warn'}
            />
            {/* Only where the label carries them, matching how they are
                scored: a field the page never gave has nothing to report. */}
            {score.form.expected > 0 && <RunMetric label="Forma" value={pct(score.form.f1)} />}
            {score.quantity.expected > 0 && (
              <RunMetric label="Volume" value={pct(score.quantity.f1)} />
            )}
          </div>
        )
      }
      notes={
        result === undefined ? undefined : (
          <p className="text-tSmall text-text-weak">
            Uma execução, não uma medição — o baseline é o <code>eval:reader</code>, que faz a média
            de várias.
            {result.draft &&
              ' O gabarito ainda está em rascunho, então o número diz tanto sobre ele quanto sobre a leitura.'}
            {!result.detected &&
              ' Sem o detector: a leitura foi feita sem blocos, e não se compara a um baseline feito com eles.'}
          </p>
        )
      }
      onClose={onClose}
    >
      {score !== undefined && <ScoreRows rows={score.rows} />}
    </RunModal>
  )
}
